import React, { lazy, Suspense, useState } from 'react';
import _ from 'lodash';
import { object } from 'prop-types';
import { I18n, getContext } from '@fiverr-private/fiverr_context';
import { metric } from '@fiverr-private/perseus';
import classNames from 'classnames';
import { Button, Icon } from '@fiverr-private/fit';
import { l_arrow_right } from '@fiverr-private/icons';
import {
    MP_CLICKED_CUSTOM_ORDER_REQUEST,
    MP_CUSTOM_ORDER_SUBMIT,
    MP_CLOSE_CUSTOM_ORDER_REQUEST
} from '../../../utils/mixPanelEvents';
import { BQ_CLICKED_CUSTOM_ORDER, BQ_SIGNUP_CLICK } from '../../../utils/bigQueryEvents';
import { sendGraphiteEvent } from '../../../utils/sendGraphiteEvent';
import { CUSTOM_ORDER_SUBMIT } from '../../../utils/sendGraphiteEvent/events';
import { CUSTOM_ORDER_SOURCE } from '../../../common/customOrder/constants';
import { getFormData } from '../api';
import { isLoggedInUser } from '../../../utils/isLoggedInUser';
import { FIT_MODAL_CLASS, JS_JOIN_HOOK } from '../../../utils/constants';
import { VARIANTS } from '../../../types/variants';
import { allocateToBriefsExperiment, redirectToCreateBriefPage } from './utils/briefs';

import './style.scss';

const CustomOrderModal = lazy(() =>
    import(/* webpackChunkName: 'CustomOrderModal' */'@fiverr-private/custom_order/src/entries/CustomOrderModalEntry')
);

const CustomOrder = ({
    titleKey,
    showArrow,
    onReportClick,
    onSubmitSuccess,
    onClose
},
{
    pathfinderWrapper,
    biEvents,
    currencyConverter,
    general
}) => {
    const [formData, setFormData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [openModal, setOpenModal] = useState(() => _.noop);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const uploadOptions = {
        objectName: 'message',
        getPolicyUrl: pathfinderWrapper.pathfinder('messages_attachments_policy'),
        createPendingAttachmentIdUrl: pathfinderWrapper.pathfinder('messages_create_pending_attachment')
    };

    const isGuest = !isLoggedInUser();
    const biEnrichmentData = biEvents.getEnrichmentData();

    const onClickReport = () => {
        if (onReportClick) {
            onReportClick(biEvents);
        } else {
            biEvents.sendMixPanelEvent({ eventName: MP_CLICKED_CUSTOM_ORDER_REQUEST });
            biEvents.sendBigQueryEvent({ eventName: BQ_CLICKED_CUSTOM_ORDER });
            metric.count('custom_order.click');
        }
    };

    const onClick = async() => {
        onClickReport();

        if (isGuest) {
            // The event BQ_SIGNUP_CLICK is reported here because we open the signUpModal manually
            // and not via the autoShowSignUp API

            biEvents.sendBigQueryEvent({ eventName: BQ_SIGNUP_CLICK });
            window.openSignUpModal();

            return;
        }

        const briefsVariation = await allocateToBriefsExperiment(pathfinderWrapper);

        if (briefsVariation === VARIANTS.B){
            redirectToCreateBriefPage(pathfinderWrapper);
            return;
        }

        if (isValidFormData()) {
            openModal();
            return;
        }

        setIsLoading(true);

        try {
            const { data } = await getFormData({ pathfinderWrapper });
            setIsLoading(false);
            setFormData(data);
        } catch {
            setIsLoading(false);
        }
    };

    const setOpenModalHandle = (open) => {
        setOpenModal(() => open);
    };

    const isValidFormData = () => {
        const { seller, categoriesData } = formData;

        return !!(seller && categoriesData);
    };

    const handleClose = () => {
        if (!isSubmitted) {
            if (onClose) {
                onClose();
            } else {
                biEvents.sendMixPanelEvent({ eventName: MP_CLOSE_CUSTOM_ORDER_REQUEST });
            }
        }
    };

    const handleSubmitSuccess = () => {
        setIsSubmitted(true);
        if (onSubmitSuccess) {
            onSubmitSuccess();
        } else {
            sendGraphiteEvent(CUSTOM_ORDER_SUBMIT);
            biEvents.sendMixPanelEvent({ eventName: MP_CUSTOM_ORDER_SUBMIT });
        }
    };

    const { pathParameters } = getContext();
    const { slug } = pathParameters;
    const { currency } = currencyConverter;
    const { seller, categoriesData } = formData;
    const submitPath = pathfinderWrapper.pathfinder('create_custom_order_request');

    const btnColor = general.isPro ? Button.COLORS.BLACK : Button.COLORS.GREY;
    const triggerButtonClass = classNames('btn-get-quote', {
        [JS_JOIN_HOOK]: isGuest
    });

    return (
        <div className="custom-order">
            <h6><I18n k={'gig_page_perseus.custom_order.title'}/></h6>
            <Button onClick={onClick}
                className={triggerButtonClass}
                fill={Button.FILLS.GHOST}
                color={btnColor}
                size={Button.SIZES.SMALL}
                disabled={isLoading}>
                <I18n k={titleKey}/>
                {showArrow &&
                <Icon size={12}>{l_arrow_right}</Icon>
                }
            </Button>
            {isValidFormData() &&
            <Suspense fallback={<div/>}>
                <CustomOrderModal name={FIT_MODAL_CLASS}
                    gigSlug={slug}
                    source={CUSTOM_ORDER_SOURCE}
                    autoOpen={true}
                    trigger={setOpenModalHandle}
                    currency={currency}
                    seller={seller}
                    categoriesData={categoriesData}
                    submitPath={submitPath}
                    uploadOptions={uploadOptions}
                    biEnrichmentData={biEnrichmentData}
                    onSubmitSuccess={handleSubmitSuccess}
                    onClose={handleClose}/>
            </Suspense>
            }
        </div>
    );
};

CustomOrder.contextTypes = {
    biEvents: object,
    pathfinderWrapper: object,
    currencyConverter: object,
    general: object
};

export default CustomOrder;
